/**
 * AxLabs Experiments - Ohm's Law
 *
 * Demonstrates the relationship between voltage, current, and resistance.
 * Includes power dissipation and V-I curve plotting.
 *
 * Governing equation: V = IR
 */
import { BaseSimulationEngine, DIMENSIONS, } from '@axlabs/simulation-core';
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'voltage',
        name: 'Applied Voltage',
        symbol: 'V',
        unit: 'V',
        dimension: DIMENSIONS.ELECTRIC_POTENTIAL,
        min: 0,
        max: 240,
        default: 12,
        step: 0.5,
        description: 'Voltage applied across the resistor',
    },
    {
        id: 'resistance',
        name: 'Resistance',
        symbol: 'R',
        unit: 'Ω',
        dimension: DIMENSIONS.ELECTRIC_RESISTANCE,
        min: 0.1,
        max: 10000,
        default: 100,
        step: 1,
        description: 'Electrical resistance',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'ohms_law',
        name: 'Ohm\'s Law',
        latex: 'V = IR',
        variables: ['voltage', 'current', 'resistance'],
        description: 'Fundamental relationship between voltage, current, and resistance',
    },
    {
        id: 'current',
        name: 'Current',
        latex: 'I = \\frac{V}{R}',
        variables: ['current', 'voltage', 'resistance'],
        description: 'Current as a function of voltage and resistance',
    },
    {
        id: 'power_vi',
        name: 'Power (V × I)',
        latex: 'P = VI',
        variables: ['power', 'voltage', 'current'],
        description: 'Power dissipated as product of voltage and current',
    },
    {
        id: 'power_i2r',
        name: 'Power (I²R)',
        latex: 'P = I^2R',
        variables: ['power', 'current', 'resistance'],
        description: 'Power in terms of current and resistance (Joule heating)',
    },
    {
        id: 'power_v2r',
        name: 'Power (V²/R)',
        latex: 'P = \\frac{V^2}{R}',
        variables: ['power', 'voltage', 'resistance'],
        description: 'Power in terms of voltage and resistance',
    },
    {
        id: 'energy',
        name: 'Energy Dissipated',
        latex: 'E = Pt',
        variables: ['energyDissipated', 'power'],
        description: 'Total energy dissipated over time',
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'excessive_power',
        name: 'Excessive Power Dissipation',
        description: 'Power exceeds typical resistor rating',
        check: (state) => (state.variables['power'] ?? 0) > 100,
        severity: 'warning',
        educationalMessage: 'Power dissipation exceeds 100W. Most standard resistors are rated for 0.25W to 2W. ' +
            'This would cause the resistor to overheat and potentially fail.',
    },
    {
        id: 'very_high_current',
        name: 'Very High Current',
        description: 'Current exceeds safe levels',
        check: (state) => (state.variables['current'] ?? 0) > 10,
        severity: 'warning',
        educationalMessage: 'Current exceeds 10A. This requires heavy-duty wiring and can be dangerous. ' +
            'Standard household circuits are typically rated for 15-20A maximum.',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const OHMS_LAW_CONFIG = {
    id: 'ohms-law',
    slug: 'ohms-law',
    name: 'Ohm\'s Law',
    domain: 'physics',
    description: 'Explore the relationship between voltage, current, and resistance',
    longDescription: `
Ohm's Law is a fundamental principle of electrical circuits, stating that the current
through a conductor is directly proportional to the voltage across it.

Key concepts:
- V = IR (voltage = current × resistance)
- For a fixed resistance, current increases linearly with voltage
- Resistance is the ratio of voltage to current
- Power dissipation: P = VI = I²R = V²/R
- Real resistors have power ratings (typically 0.25W to 2W)
- Exceeding power ratings causes overheating

This simulation allows you to explore:
- How current changes with voltage (V-I characteristics)
- Power dissipation and energy consumption
- The effect of resistance on circuit behavior
  `.trim(),
    version: 1,
    conceptTags: ['electricity', 'circuits', 'resistance', 'power'],
    difficulty: 2,
    prerequisites: ['basic-electricity'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [],
    failureModes: FAILURE_MODES,
    recommendedDt: 1 / 60,
    maxDt: 1 / 10,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class OhmsLawSimulation extends BaseSimulationEngine {
    config = OHMS_LAW_CONFIG;
    constructor() {
        super({ voltage: 0, current: 0, power: 0, energyDissipated: 0 }, { type: 'euler', dt: 1 / 60, maxSubsteps: 1 });
    }
    getInitialState(params) {
        const V = params['voltage'] ?? 12;
        const R = params['resistance'] ?? 100;
        const I = V / R;
        const P = V * I;
        return {
            voltage: V,
            current: I,
            power: P,
            energyDissipated: 0,
        };
    }
    computeDerivatives(state, _params) {
        // Energy increases at rate = power
        return {
            energyDissipated: state.power,
        };
    }
    integrate(dt) {
        const V = this.parameters['voltage'] ?? 12;
        const R = this.parameters['resistance'] ?? 100;
        const I = V / R;
        const P = V * I;
        this.state.voltage = V;
        this.state.current = I;
        this.state.power = P;
        this.state.energyDissipated += P * dt;
    }
    getPositionForStabilityCheck() {
        return this.state.current;
    }
    /**
     * Generate V-I curve data for a range of voltages
     */
    generateVICurve(voltages) {
        const R = this.parameters['resistance'] ?? 100;
        return voltages.map(V => ({
            voltage: V,
            current: V / R,
        }));
    }
    /**
     * Calculate temperature rise (simplified model)
     * Assumes resistor with thermal mass and cooling
     */
    estimateTemperatureRise(ambientTemp, thermalResistance // °C/W
    ) {
        const P = this.state.power;
        return ambientTemp + P * thermalResistance;
    }
}
/**
 * Factory function to create a new Ohm's Law simulation
 */
export function createOhmsLaw() {
    return new OhmsLawSimulation();
}
//# sourceMappingURL=ohms-law.js.map